import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
import {
  hydratePatientProfileAvatar,
  loadPatientIdentityContextsByAppointmentId,
  PATIENT_AVATAR_PLACEHOLDER,
} from "@/services/patientIdentityService.js";
import { getSupabaseBaseUrl } from "@/services/supabaseClient";
import { pickDiagnosisTitle } from "@/utils/diagnosis.js";

const PATIENT_AVATAR_BUCKET = "patient-profile-images";
const DOCTOR_AVATAR_BUCKET = "doctor-profile-images";

function pickFirst(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function fullNameFromParts(firstName, lastName) {
  return [pickFirst(firstName), pickFirst(lastName)].filter(Boolean).join(" ").trim();
}

function resolvePublicStorageUrl(bucket, rawPath) {
  const path = String(rawPath || "").trim();
  if (!path) return "";
  if (/^(?:https?:|data:|blob:)/i.test(path)) return path;

  const base = getSupabaseBaseUrl();
  const cleanPath = path.replace(/^public\//, "").replace(/^\/+/, "");
  if (!base || !cleanPath) return "";

  return `${base}/storage/v1/object/public/${bucket}/${cleanPath}`;
}

export function resolvePatientAvatar(record) {
  if (!record || typeof record !== "object") return "";

  return pickFirst(
    record?.resolved_avatar_url,
    record?.profile_image_url,
    record?.profile_photo_url,
    record?.photo_url,
    resolvePublicStorageUrl(
      PATIENT_AVATAR_BUCKET,
      pickFirst(
        record?.profile_photo_path,
        record?.profile_photo_storage_path,
        record?.photo_path,
        record?.avatar_path,
      ),
    ),
    record?.avatar_url,
  );
}

function resolveDoctorLikeAvatar(record) {
  if (!record || typeof record !== "object") return "";

  return pickFirst(
    record?.resolved_avatar_url,
    record?.avatar_url,
    record?.profile_image_url,
    record?.profile_photo_url,
    record?.photo_url,
    resolvePublicStorageUrl(
      DOCTOR_AVATAR_BUCKET,
      pickFirst(
        record?.profile_photo_path,
        record?.profile_photo_storage_path,
        record?.photo_path,
        record?.avatar_path,
      ),
    ),
  );
}

function pickResolvedAvatar(...values) {
  for (const value of values) {
    const avatar = String(value || "").trim();
    if (!avatar) continue;
    if (avatar === PATIENT_AVATAR_PLACEHOLDER) continue;
    return avatar;
  }
  return "";
}

function toDate(value) {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime()) ? null : date;
}

function countPrescriptionItems(meta) {
  const candidates = [
    meta?.items,
    meta?.prescriptionItems,
    meta?.medications,
    meta?.medicationItems,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate.length;
    }
  }

  return pickFirst(meta?.itemsCount, meta?.itemCount, meta?.lineItemsCount) || "—";
}

function formatRefill(meta) {
  const direct = pickFirst(meta?.refillCount, meta?.refills, meta?.repeatCount);
  if (direct) return direct;

  const monthly = pickFirst(meta?.repeatMonths, meta?.refillMonths);
  if (monthly) return `${monthly} mo`;

  return "—";
}

function hasValidPrescriptionLineage(row, appointmentById, patientUserByRecordId, doctorIds) {
  const appointmentId = String(row?.appointment_id || "").trim();
  if (!appointmentId) return false;

  const appointment = appointmentById.get(appointmentId);
  if (!appointment) return false;

  const appointmentDoctorId = String(appointment?.doctor_id || "").trim();
  if (!appointmentDoctorId || !doctorIds.has(appointmentDoctorId)) return false;

  const appointmentPatientRecordId = String(appointment?.patient_id || "").trim();
  const appointmentPatientUserId = String(
    patientUserByRecordId.get(appointmentPatientRecordId) || "",
  ).trim();
  const prescriptionPatientUserId = String(row?.patient_user_id || "").trim();

  return !!appointmentPatientUserId &&
    !!prescriptionPatientUserId &&
    appointmentPatientUserId === prescriptionPatientUserId;
}

export async function listDoctorPrescriptions(client, userId, { limit = 100 } = {}) {
  const authUserId = String(userId || "").trim();
  if (!authUserId) return [];

  const { data: doctorProfile } = await client
    .from("doctor_profiles")
    .select("id")
    .eq("user_id", authUserId)
    .maybeSingle();

  const doctorIds = Array.from(
    new Set([authUserId, String(doctorProfile?.id || "").trim()].filter(Boolean)),
  );
  const doctorIdSet = new Set(doctorIds);

  const { data: prescriptions, error: rxError } = await client
    .from("prescriptions")
    .select(
      "id, appointment_id, patient_user_id, status, issued_at, expires_at, created_at, external_rx_id, diagnosis, notes, meta",
    )
    .in("doctor_user_id", doctorIds)
    .order("issued_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(limit);

  if (rxError) throw rxError;

  const list = Array.isArray(prescriptions) ? prescriptions : [];
  const appointmentIds = Array.from(
    new Set(list.map((row) => String(row?.appointment_id || "").trim()).filter(Boolean)),
  );

  const appointmentsRes = appointmentIds.length
    ? await client
        .from("appointments")
        .select("id, patient_id, doctor_id, diagnosis_title, diagnosis_notes, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship")
        .in("id", appointmentIds)
    : { data: [] };

  const appointmentIdentityById = await loadPatientIdentityContextsByAppointmentId(
    appointmentsRes?.data || [],
    client,
  );
  const appointmentById = new Map(
    (Array.isArray(appointmentsRes?.data) ? appointmentsRes.data : []).map((row) => [
      String(row?.id || "").trim(),
      row,
    ]),
  );
  const patientRefs = Array.from(
    new Set(
      list
        .flatMap((row) => [
          String(appointmentById.get(String(row?.appointment_id || "").trim())?.patient_id || "").trim(),
          String(row?.patient_user_id || "").trim(),
          String(row?.meta?.patientRecordId || "").trim(),
          String(row?.meta?.patientAuthUserId || "").trim(),
          String(
            appointmentIdentityById.get(String(row?.appointment_id || "").trim())?.patientUserId || "",
          ).trim(),
        ])
        .filter(Boolean),
    ),
  );

  const { data: patientRows } = patientRefs.length
    ? await client.from("patients").select("id, user_id").in("id", patientRefs)
    : { data: [] };

  const patientUserIds = Array.from(
    new Set(
      patientRefs
        .concat((Array.isArray(patientRows) ? patientRows : []).map((row) => String(row?.user_id || "").trim()))
        .filter(Boolean),
    ),
  );

  const [profilesRes, patientProfilesRes, doctorProfilesRes] = await Promise.all([
    patientUserIds.length
      ? client
          .from("profiles")
          .select("id, user_id, display_name, avatar_url")
          .in("user_id", patientUserIds)
      : Promise.resolve({ data: [] }),
    patientUserIds.length
      ? client
          .from("patient_profiles")
          .select(
            "user_id, full_name, first_name, last_name, profile_photo_path, profile_photo_storage_path, profile_image_url, avatar_url, national_id, national_id_number",
          )
          .in("user_id", patientUserIds)
      : Promise.resolve({ data: [] }),
    patientUserIds.length
      ? client
          .from("doctor_profiles")
          .select(
            "user_id, full_name, first_name, last_name, profile_photo_path, profile_image_url, register_number, medical_license_number",
          )
          .in("user_id", patientUserIds)
      : Promise.resolve({ data: [] }),
  ]);

  const patientUserByRecordId = new Map();
  (Array.isArray(patientRows) ? patientRows : []).forEach((row) => {
    patientUserByRecordId.set(String(row?.id || "").trim(), String(row?.user_id || "").trim());
  });

  const validList = list.filter((row) =>
    hasValidPrescriptionLineage(row, appointmentById, patientUserByRecordId, doctorIdSet),
  );

  const patientInfoByUserId = new Map();
  (Array.isArray(profilesRes?.data) ? profilesRes.data : []).forEach((row) => {
    const userKey = String(row?.user_id || "").trim();
    patientInfoByUserId.set(userKey, {
      name: pickFirst(
        row?.display_name,
        "",
      ),
      avatarUrl: resolvePatientAvatar(row),
      secondary: String(row?.id || "").trim(),
    });
  });
  const hydratedPatientProfiles = await Promise.all(
    (Array.isArray(patientProfilesRes?.data) ? patientProfilesRes.data : []).map((row) =>
      hydratePatientProfileAvatar(row, client),
    ),
  );
  hydratedPatientProfiles.forEach((row) => {
    const userKey = String(row?.user_id || "").trim();
    const prior = patientInfoByUserId.get(userKey) || {};
    patientInfoByUserId.set(userKey, {
      name:
        prior.name ||
        pickFirst(row?.full_name, fullNameFromParts(row?.first_name, row?.last_name)),
      // `profiles.avatar_url` is often the generic account fallback. The
      // authorised patient-profile image is canonical when it is available.
      avatarUrl: resolvePatientAvatar(row) || prior.avatarUrl,
      secondary:
        prior.secondary ||
        pickFirst(row?.national_id, row?.national_id_number, userKey),
    });
  });
  (Array.isArray(doctorProfilesRes?.data) ? doctorProfilesRes.data : []).forEach((row) => {
    const userKey = String(row?.user_id || "").trim();
    const prior = patientInfoByUserId.get(userKey) || {};
    patientInfoByUserId.set(userKey, {
      name:
        prior.name ||
        pickFirst(row?.full_name, fullNameFromParts(row?.first_name, row?.last_name)),
      avatarUrl: prior.avatarUrl || resolveDoctorLikeAvatar(row),
      secondary:
        prior.secondary ||
        pickFirst(row?.register_number, row?.medical_license_number, userKey),
    });
  });

  return validList.map((row) => {
    const meta = row?.meta || {};
    const patientRef = String(meta?.patientAuthUserId || row?.patient_user_id || "").trim();
    const resolvedPatientUserId = patientUserByRecordId.get(patientRef) || patientRef;
    const appointmentIdentity =
      appointmentIdentityById.get(String(row?.appointment_id || "").trim()) || null;
    const appointmentRow =
      appointmentById.get(String(row?.appointment_id || "").trim()) || null;
    const patientInfo =
      patientInfoByUserId.get(
        String(appointmentIdentity?.patientUserId || resolvedPatientUserId || "").trim(),
      ) || {};
    const careSubjectType = String(
      appointmentIdentity?.careSubjectType || meta?.careSubjectType || "self",
    )
      .trim()
      .toLowerCase();
    const issuedAt = row?.issued_at || row?.created_at || "";

    return {
      id: String(row?.id || "").trim(),
      appointmentId: String(row?.appointment_id || "").trim(),
      rxNo: prescriptionIdentifier(row),
      patientName:
        appointmentIdentity?.patientName || patientInfo.name || pickFirst(meta?.patientName, "Patient"),
      patientAvatarUrl:
        pickResolvedAvatar(
          appointmentIdentity?.patientAvatar,
          patientInfo.avatarUrl,
          meta?.patientAvatarUrl,
          meta?.patient_avatar_url,
        ),
      patientSecondary: pickFirst(
        meta?.patientNationalId,
        careSubjectType === "dependant" ? meta?.responsibleRelationship : "",
        patientInfo.secondary,
        appointmentIdentity?.patientRowId,
        appointmentIdentity?.patientUserId,
        meta?.patientRecordId,
        "",
      ),
      condition:
        pickDiagnosisTitle(
          appointmentRow?.diagnosis_title,
        ) || "No diagnosis recorded",
      issuedAt,
      expiresAt: row?.expires_at || meta?.endsOn || "",
      refill: formatRefill(meta),
      status: row?.status || "draft",
      itemsCount: countPrescriptionItems(meta),
    };
  });
}

export function countPrescriptionsLast30Days(rows, now = new Date()) {
  const end = new Date(now);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);

  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const issued = toDate(row?.issuedAt);
    return !!issued && issued >= start && issued <= end;
  }).length;
}
